import AddNotesIcon from '@assets/images/icons/add-notes.svg';
import BookIcon from '@assets/images/icons/book.svg';
import DebrisIcon from '@assets/images/icons/debris.svg';
import ExportIcon from '@assets/images/icons/export.svg';
import LockIcon from '@assets/images/icons/lock.svg';
import LockedForumIcon from '@assets/images/icons/locked-forum.svg';
import NotValidIcon from '@assets/images/icons/not-valid.svg';
import OperationIcon from '@assets/images/icons/operation.svg';
import StatusIcon from '@assets/images/icons/status.svg';
import TrajectoryIcon from '@assets/images/icons/trajectory.svg';
import WiseIdIcon from '@assets/images/icons/wiseid.svg';

import {
  LiveHelpOutlined,
  CheckBoxOutlineBlank,
  CheckBoxOutlined,
  AddOutlined,
  CancelOutlined,
  CheckOutlined,
  PhoneOutlined,
  CloseOutlined,
  DeleteOutlined,
  UploadOutlined,
  DownloadOutlined,
  InfoOutlined,
  CalendarTodayOutlined,
  VisibilityOutlined,
  VisibilityOffOutlined,
  BusinessOutlined,
  ArrowBackOutlined,
  ArrowForwardOutlined,
  SendOutlined,
  CircleOutlined,
  MoreHorizOutlined,
  MoreVertOutlined,
  ReportOutlined,
  DescriptionOutlined,
  AddBoxOutlined,
  RemoveOutlined,
  ViewModuleOutlined,
  ChatOutlined,
  FolderCopyOutlined,
  GroupOutlined,
  PersonOutlined,
  LogoutOutlined,
  FilterListOutlined,
  KeyboardArrowDownOutlined,
  KeyboardArrowUpOutlined,
  SearchOutlined,
  ChatBubbleOutlined,
  EditOutlined,
  AccessTimeOutlined,
  ForumOutlined,
  ChevronRightOutlined,
  ChevronLeftOutlined,
  HelpOutlineOutlined,
  ListAlt,
  Satellite,
  CorporateFare,
  CheckCircleOutlined,
  SettingsOutlined,
  Groups3Outlined,
  SatelliteAlt,
  AssignmentIndOutlined,
  WarningAmber,
  AccountCircleOutlined,
  LockOutlined,
  NoteAddOutlined,
  VpnLock,
  Public,
  Verified,
} from '@mui/icons-material';
import { styled } from '@mui/joy';
import { SvgIconProps } from '@mui/material';
import { FC, SVGProps, cloneElement, type JSX } from 'react';

enum IconTypes {
  IBM = 'IBM',
  SVG = 'SVG',
  MATERIAL = 'MATERIAL',
}

type SvgComponent = FC<SVGProps<SVGSVGElement>>;
interface IconSpecFA {
  Component: JSX.Element;
  type: IconTypes.IBM;
}
interface IconSpecSVG {
  Component: SvgComponent;
  type: IconTypes.SVG;
}
interface IconSpecMaterial {
  Component: FC<SvgIconProps>;
  type: IconTypes.MATERIAL;
}

type IconSpec = IconSpecFA | IconSpecSVG | IconSpecMaterial;

/** List of icons */
export enum IconName {
  assignment = 'assignment',
  liveHelp = 'liveHelp',
  settings = 'settings',
  group = 'group',
  checkBox = 'checkBox',
  checkBoxOutlineBlank = 'checkBoxOutlineBlank',
  add = 'add',
  addNotes = 'addNotes',
  noteAdd = 'noteAdd',
  check = 'check',
  checkmark = 'checkmark',
  filter = 'filter',
  checkCircle = 'checkCircle',
  phone = 'phone',
  close = 'close',
  delete = 'delete',
  plus = 'plus',
  upload = 'upload',
  download = 'download',
  info = 'info',
  messageInfo = 'messageInfo',
  calendar = 'calendar',
  eye = 'eye',
  visibility = 'visibility',
  visibilityOff = 'visibilityOff',
  company = 'company',
  arrowLeft = 'arrowLeft',
  arrowRight = 'arrowRight',
  send = 'send',
  closeCircle = 'closeCircle',
  dotsMenu = 'dotsMenu',
  moreVertical = 'moreVertical',
  report = 'report',
  documentValid = 'documentValid',
  documentNotValid = 'documentNotValid',
  document = 'document',
  blocks = 'blocks',
  messages = 'messages',
  folders = 'folders',
  people = 'people',
  profile = 'profile',
  profileCircle = 'profileCircle',
  help = 'help',
  logOut = 'logOut',
  chevronDown = 'chevronDown',
  chevronUp = 'chevronUp',
  search = 'search',
  feedback = 'feedback',
  comments = 'comments',
  edit = 'edit',
  addBlock = 'addBlock',
  time = 'time',
  dedicatedForum = 'dedicatedForum',
  chevronRight = 'chevronRight',
  chevronLeft = 'chevronLeft',
  warning = 'warning',
  lock = 'lock',
  lockedForum = 'lockedForum',
  lockOutlined = 'lockOutlined',
  notValid = 'notValid',
  vpn_lock_2 = 'vpn_lock_2',
  public = 'public',
  Group = 'Group',
  satellite = 'satellite',
  export = 'export',
  operation = 'operation',
  status = 'status',
  listAlt = 'listAlt',
  trajectory = 'trajectory',
  book = 'book',
  cancel = 'cancel',
  corporate = 'corporate',
  satelliteAlt = 'satelliteAlt',
  debris = 'debris',
  verified = 'verified',
  wiseid = 'wiseid',
}

/** Map icons to their visual asset */
const SvgMap: Record<IconName, IconSpec> = {
  [IconName.assignment]: {
    Component: AssignmentIndOutlined as FC<SvgIconProps>,
    type: IconTypes.MATERIAL,
  },
  [IconName.liveHelp]: {
    Component: LiveHelpOutlined as FC<SvgIconProps>,
    type: IconTypes.MATERIAL,
  },
  [IconName.add]: {
    Component: AddOutlined as FC<SvgIconProps>,
    type: IconTypes.MATERIAL,
  },
  [IconName.settings]: {
    Component: SettingsOutlined as FC<SvgIconProps>,
    type: IconTypes.MATERIAL,
  },
  [IconName.group]: {
    Component: Groups3Outlined as FC<SvgIconProps>,
    type: IconTypes.MATERIAL,
  },
  [IconName.checkBox]: {
    Component: CheckBoxOutlined as FC<SvgIconProps>,
    type: IconTypes.MATERIAL,
  },
  [IconName.checkBoxOutlineBlank]: {
    Component: CheckBoxOutlineBlank as FC<SvgIconProps>,
    type: IconTypes.MATERIAL,
  },
  [IconName.addNotes]: {
    Component: AddNotesIcon as SvgComponent,
    type: IconTypes.SVG,
  },
  [IconName.noteAdd]: {
    Component: NoteAddOutlined as FC<SvgIconProps>,
    type: IconTypes.MATERIAL,
  },
  [IconName.filter]: {
    Component: FilterListOutlined as FC<SvgIconProps>,
    type: IconTypes.MATERIAL,
  },
  [IconName.checkmark]: {
    Component: CheckOutlined as FC<SvgIconProps>,
    type: IconTypes.MATERIAL,
  },
  [IconName.check]: {
    Component: CheckOutlined as FC<SvgIconProps>,
    type: IconTypes.MATERIAL,
  },
  [IconName.checkCircle]: {
    Component: CheckCircleOutlined as FC<SvgIconProps>,
    type: IconTypes.MATERIAL,
  },
  [IconName.phone]: {
    Component: PhoneOutlined as FC<SvgIconProps>,
    type: IconTypes.MATERIAL,
  },
  [IconName.close]: {
    Component: CloseOutlined as FC<SvgIconProps>,
    type: IconTypes.MATERIAL,
  },
  [IconName.delete]: {
    Component: DeleteOutlined as FC<SvgIconProps>,
    type: IconTypes.MATERIAL,
  },
  [IconName.plus]: {
    Component: AddOutlined as FC<SvgIconProps>,
    type: IconTypes.MATERIAL,
  },
  [IconName.upload]: {
    Component: UploadOutlined as FC<SvgIconProps>,
    type: IconTypes.MATERIAL,
  },
  [IconName.download]: {
    Component: DownloadOutlined as FC<SvgIconProps>,
    type: IconTypes.MATERIAL,
  },
  [IconName.info]: {
    Component: InfoOutlined as FC<SvgIconProps>,
    type: IconTypes.MATERIAL,
  },
  [IconName.messageInfo]: {
    Component: InfoOutlined as FC<SvgIconProps>,
    type: IconTypes.MATERIAL,
  },
  [IconName.calendar]: {
    Component: CalendarTodayOutlined as FC<SvgIconProps>,
    type: IconTypes.MATERIAL,
  },
  [IconName.eye]: {
    Component: VisibilityOutlined as FC<SvgIconProps>,
    type: IconTypes.MATERIAL,
  },
  [IconName.visibility]: {
    Component: VisibilityOutlined as FC<SvgIconProps>,
    type: IconTypes.MATERIAL,
  },
  [IconName.visibilityOff]: {
    Component: VisibilityOffOutlined as FC<SvgIconProps>,
    type: IconTypes.MATERIAL,
  },
  [IconName.company]: {
    Component: BusinessOutlined as FC<SvgIconProps>,
    type: IconTypes.MATERIAL,
  },
  [IconName.arrowLeft]: {
    Component: ArrowBackOutlined as FC<SvgIconProps>,
    type: IconTypes.MATERIAL,
  },
  [IconName.arrowRight]: {
    Component: ArrowForwardOutlined as FC<SvgIconProps>,
    type: IconTypes.MATERIAL,
  },
  [IconName.send]: {
    Component: SendOutlined as FC<SvgIconProps>,
    type: IconTypes.MATERIAL,
  },
  [IconName.closeCircle]: {
    Component: CircleOutlined as FC<SvgIconProps>,
    type: IconTypes.MATERIAL,
  },
  [IconName.dotsMenu]: {
    Component: MoreHorizOutlined as FC<SvgIconProps>,
    type: IconTypes.MATERIAL,
  },
  [IconName.moreVertical]: {
    Component: MoreVertOutlined as FC<SvgIconProps>,
    type: IconTypes.MATERIAL,
  },
  [IconName.report]: {
    Component: ReportOutlined as FC<SvgIconProps>,
    type: IconTypes.MATERIAL,
  },
  [IconName.documentValid]: {
    Component: AddBoxOutlined as FC<SvgIconProps>,
    type: IconTypes.MATERIAL,
  },
  [IconName.documentNotValid]: {
    Component: RemoveOutlined as FC<SvgIconProps>,
    type: IconTypes.MATERIAL,
  },
  [IconName.document]: {
    Component: DescriptionOutlined as FC<SvgIconProps>,
    type: IconTypes.MATERIAL,
  },
  [IconName.blocks]: {
    Component: ViewModuleOutlined as FC<SvgIconProps>,
    type: IconTypes.MATERIAL,
  },
  [IconName.messages]: {
    Component: ChatOutlined as FC<SvgIconProps>,
    type: IconTypes.MATERIAL,
  },
  [IconName.listAlt]: {
    Component: ListAlt as FC<SvgIconProps>,
    type: IconTypes.MATERIAL,
  },
  [IconName.folders]: {
    Component: FolderCopyOutlined as FC<SvgIconProps>,
    type: IconTypes.MATERIAL,
  },
  [IconName.people]: {
    Component: GroupOutlined as FC<SvgIconProps>,
    type: IconTypes.MATERIAL,
  },
  [IconName.profile]: {
    Component: PersonOutlined as FC<SvgIconProps>,
    type: IconTypes.MATERIAL,
  },
  [IconName.profileCircle]: {
    Component: AccountCircleOutlined as FC<SvgIconProps>,
    type: IconTypes.MATERIAL,
  },
  [IconName.help]: {
    Component: HelpOutlineOutlined as FC<SvgIconProps>,
    type: IconTypes.MATERIAL,
  },
  [IconName.book]: {
    Component: BookIcon as SvgComponent,
    type: IconTypes.SVG,
  },
  [IconName.logOut]: {
    Component: LogoutOutlined as FC<SvgIconProps>,
    type: IconTypes.MATERIAL,
  },
  [IconName.chevronDown]: {
    Component: KeyboardArrowDownOutlined as FC<SvgIconProps>,
    type: IconTypes.MATERIAL,
  },
  [IconName.chevronUp]: {
    Component: KeyboardArrowUpOutlined as FC<SvgIconProps>,
    type: IconTypes.MATERIAL,
  },
  [IconName.search]: {
    Component: SearchOutlined as FC<SvgIconProps>,
    type: IconTypes.MATERIAL,
  },
  [IconName.feedback]: {
    Component: ChatBubbleOutlined as FC<SvgIconProps>,
    type: IconTypes.MATERIAL,
  },
  [IconName.comments]: {
    Component: ChatOutlined as FC<SvgIconProps>,
    type: IconTypes.MATERIAL,
  },
  [IconName.edit]: {
    Component: EditOutlined as FC<SvgIconProps>,
    type: IconTypes.MATERIAL,
  },
  [IconName.addBlock]: {
    Component: AddBoxOutlined as FC<SvgIconProps>,
    type: IconTypes.MATERIAL,
  },
  [IconName.time]: {
    Component: AccessTimeOutlined as FC<SvgIconProps>,
    type: IconTypes.MATERIAL,
  },
  [IconName.dedicatedForum]: {
    Component: ForumOutlined as FC<SvgIconProps>,
    type: IconTypes.MATERIAL,
  },
  [IconName.chevronRight]: {
    Component: ChevronRightOutlined as FC<SvgIconProps>,
    type: IconTypes.MATERIAL,
  },
  [IconName.chevronLeft]: {
    Component: ChevronLeftOutlined as FC<SvgIconProps>,
    type: IconTypes.MATERIAL,
  },

  [IconName.warning]: {
    Component: WarningAmber as FC<SvgIconProps>,
    type: IconTypes.MATERIAL,
  },
  [IconName.export]: {
    Component: ExportIcon as SvgComponent,
    type: IconTypes.SVG,
  },
  [IconName.lock]: {
    Component: LockIcon as SvgComponent,
    type: IconTypes.SVG,
  },
  [IconName.lockedForum]: {
    Component: LockedForumIcon as SvgComponent,
    type: IconTypes.SVG,
  },
  [IconName.lockOutlined]: {
    Component: LockOutlined as FC<SvgIconProps>,
    type: IconTypes.MATERIAL,
  },
  [IconName.vpn_lock_2]: {
    Component: VpnLock as FC<SvgIconProps>,
    type: IconTypes.MATERIAL,
  },
  [IconName.public]: {
    Component: Public as FC<SvgIconProps>,
    type: IconTypes.MATERIAL,
  },
  [IconName.Group]: {
    Component: GroupOutlined as FC<SvgIconProps>,
    type: IconTypes.MATERIAL,
  },
  [IconName.notValid]: {
    Component: NotValidIcon as SvgComponent,
    type: IconTypes.SVG,
  },
  [IconName.trajectory]: {
    Component: TrajectoryIcon as SvgComponent,
    type: IconTypes.SVG,
  },
  [IconName.satellite]: {
    Component: Satellite as SvgComponent,
    type: IconTypes.SVG,
  },
  [IconName.operation]: {
    Component: OperationIcon as SvgComponent,
    type: IconTypes.SVG,
  },
  [IconName.status]: {
    Component: StatusIcon as SvgComponent,
    type: IconTypes.SVG,
  },
  [IconName.corporate]: {
    Component: CorporateFare as FC<SvgIconProps>,
    type: IconTypes.MATERIAL,
  },
  [IconName.cancel]: {
    Component: CancelOutlined as FC<SvgIconProps>,
    type: IconTypes.MATERIAL,
  },
  [IconName.satelliteAlt]: {
    Component: SatelliteAlt as FC<SvgIconProps>,
    type: IconTypes.MATERIAL,
  },
  [IconName.debris]: {
    Component: DebrisIcon as SvgComponent,
    type: IconTypes.SVG,
  },
  [IconName.verified]: {
    Component: Verified as FC<SvgIconProps>,
    type: IconTypes.MATERIAL,
  },
  [IconName.wiseid]: {
    Component: WiseIdIcon as SvgComponent,
    type: IconTypes.SVG,
  },
};

const IconWrapper = styled('div')<{ width: string; height: string; color?: string }>`
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 0;
  width: ${props => props.width};
  height: ${props => props.height};

  svg {
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${props => props.color || 'inherit'};
  }
`;

interface IconProps {
  name: IconName;
  size?: number;
  color?: string;
  width?: number;
}

/**
 * Icon component
 * @param {IconName} name - Icon name (from IconName enum)
 * @param {number} [size=20] - Icon size (in px)
 * @param {string} [color='inherit'] - Icon color (hex, rgb, rgba, hsl, hsla, etc.)
 * @param {number} [width] - Icon width (in px). If not provided, uses size
 */
const Icon = ({ color = 'inherit', name, size = 20, width }: IconProps): JSX.Element => {
  if (!SvgMap[name]) {
    // console.error(`Icon ${name} not found in SvgMap`);
    return (
      <IconWrapper height={`${size}px`} width={width ? `${width}px` : `${size}px`} color={color}>
        <InfoOutlined sx={{ color }} />
      </IconWrapper>
    );
  }

  const iconSpec = SvgMap[name];

  if (iconSpec.type === IconTypes.MATERIAL) {
    const { Component } = iconSpec;
    return (
      <IconWrapper height={`${size}px`} width={width ? `${width}px` : `${size}px`} color={color}>
        <Component sx={{ color, fontSize: `${size}px` }} />
      </IconWrapper>
    );
  }

  if (iconSpec.type === IconTypes.SVG) {
    return (
      <IconWrapper height={`${size}px`} width={width ? `${width}px` : `${size}px`} color={color}>
        <iconSpec.Component width={size} height={size} style={{ color }} />
      </IconWrapper>
    );
  }

  // Fallback for IBM icons if needed
  if (iconSpec.type === IconTypes.IBM) {
    return cloneElement(iconSpec.Component, { color, size, width });
  }

  // Default fallback if no matching type is found
  return (
    <IconWrapper height={`${size}px`} width={width ? `${width}px` : `${size}px`} color={color}>
      <InfoOutlined sx={{ color }} />
    </IconWrapper>
  );
};

export default Icon;
